'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next-nprogress-bar';
import { parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { LoginResult } from '@/app/api/login/route';
import { ButtonSubmit } from '@/components/buttons/button-submit';
import { Signpost } from '@/components/modals/signpost/signpost';
import { Form, FormField, FormItem } from '@/components/ui/form';
import { FormInput } from '@/components/ui/form-inputs/form-input';
import { useToast } from '@/components/ui/use-toast';
import { isSafeCallbackUrl, isValueEmpty } from '@/utils';

const formSchema = z.object({
  username: z.string().min(3, 'Uživatelské jméno je povinné.'),
  password: z.string().min(4, 'Heslo je povinné.'),
});

export type FormInputs = z.infer<typeof formSchema>;

export const LoginForm = () => {
  const router = useRouter();

  const [callbackUrl] = useQueryState('callbackUrl', parseAsString);
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { toast } = useToast();

  const form = useForm<FormInputs>({
    resolver: zodResolver(formSchema),
    reValidateMode: 'onBlur',
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<FormInputs> = async (data, e) => {
    e?.preventDefault();

    const response = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: data.username.trim(), password: data.password }),
    });
    const result = await response.json() as LoginResult;
    if (!result.success) {
      toast({
        title: 'Chyba',
        description: result.message,
        variant: 'destructive',
      });
      return;
    }

    queryClient.setQueryData(['getApiV1AuthMe'], result.user);
    queryClient.setQueryData(['getApiV1SignpostPlayers', ''], result.userPlayers);
    queryClient.setQueryData(['getApiV1SignpostClubs'], result.userClubs);
    if (!isValueEmpty(callbackUrl) && isSafeCallbackUrl(callbackUrl)) {
      router.push(callbackUrl);
    } else if (result.userPlayers.length > 1 || result.userClubs.length > 0) {
      setIsOpen(true);
    } else {
      router.push('/');
    }
  };

  return (
    <>
      <Signpost isOpen={isOpen} setIsOpen={setIsOpen} />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <FormField
            name="username"
            control={form.control}
            render={({ field }) => (
              <FormItem className="relative">
                <FormInput
                  label="Vaše uživatelské jméno"
                  id="username-input"
                  placeholder="Přihlašovací jméno"
                  autoComplete="username"
                  {...field}
                />
              </FormItem>
            )}
          />
          <FormField
            name="password"
            control={form.control}
            render={({ field }) => (
              <FormItem className="relative">
                <FormInput
                  label="Heslo"
                  id="password-input"
                  placeholder="Heslo"
                  autoComplete="current-password"
                  isPassword
                  {...field}
                />
              </FormItem>
            )}
          />
          <ButtonSubmit
            data-testid="cypress-login-button"
            className="mt-4 w-full"
          >
            Přihlásit se
          </ButtonSubmit>
        </form>
      </Form>
    </>

  );
};
